import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { getLastActivity, markActivity } from "@/lib/api";

const AuthCtx = createContext(null);

// Idle limit by role, ms. Driver gets a longer leash since GPS pings during a
// route already count as activity, but between rides the app can sit idle.
const IDLE_LIMITS_MS = {
  agency_admin: 15 * 60 * 1000,
  dispatcher: 15 * 60 * 1000,
  client_user: 15 * 60 * 1000,
  driver: 60 * 60 * 1000,
};
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const REFRESH_STALE_MS = 40 * 60 * 1000;
const WINDOW_ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"];

// Decode a JWT's iat (seconds) from its payload segment, no library.
function tokenIssuedAtMs(token) {
  try {
    const payload = token.split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.iat === "number" ? json.iat * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=not auth, obj=user
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("nemt_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      // Nothing to store yet, the challenge token is only good for the follow-up
      // POST /auth/mfa/verify call, not for any authenticated request.
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    localStorage.setItem("nemt_token", data.access_token);
    localStorage.setItem("nemt_user", JSON.stringify(data.user));
    markActivity(true);
    setUser(data.user);
    return data.user;
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
    localStorage.setItem("nemt_token", data.access_token);
    localStorage.setItem("nemt_user", JSON.stringify(data.user));
    markActivity(true);
    setUser(data.user);
    return data.user;
  }, []);

  // Used after /auth/mfa/enable and /auth/mfa/disable: both mint a fresh session
  // token because every older token for that account stops working the moment
  // MFA state changes, and both return the updated user alongside it.
  const applySession = useCallback((data) => {
    localStorage.setItem("nemt_token", data.access_token);
    localStorage.setItem("nemt_user", JSON.stringify(data.user));
    setUser(data.user);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    localStorage.setItem("nemt_user", JSON.stringify(data));
    setUser(data);
    return data;
  }, []);

  const registerAgency = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register-agency", payload);
    localStorage.setItem("nemt_token", data.access_token);
    localStorage.setItem("nemt_user", JSON.stringify(data.user));
    markActivity(true);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("nemt_token");
    localStorage.removeItem("nemt_user");
    setUser(false);
    window.location.href = "/login";
  }, []);

  const idleLogout = useCallback(() => {
    localStorage.removeItem("nemt_token");
    localStorage.removeItem("nemt_user");
    setUser(false);
    window.location.href = "/login?reason=idle";
  }, []);

  // Activity tracking, idle sign-out, and sliding token refresh. Only wired up
  // while a user is actually signed in; fully torn down otherwise/on unmount.
  useEffect(() => {
    if (!user) return;
    const idleLimit = IDLE_LIMITS_MS[user.role] || IDLE_LIMITS_MS.agency_admin;

    const onActivity = () => markActivity();
    WINDOW_ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onActivity);

    const idleTimer = setInterval(() => {
      if (Date.now() - getLastActivity() > idleLimit) {
        idleLogout();
      }
    }, IDLE_CHECK_INTERVAL_MS);

    const doRefresh = async () => {
      if (Date.now() - getLastActivity() > idleLimit) return;
      try {
        const { data } = await api.post("/auth/refresh");
        localStorage.setItem("nemt_token", data.access_token);
        localStorage.setItem("nemt_user", JSON.stringify(data.user));
      } catch {
        // A 401 here is handled by the response interceptor's redirect already.
      }
    };

    // Catch up on load if the stored token is already stale (e.g. tab was
    // asleep/reloaded) and the user is still within the idle window.
    const token = localStorage.getItem("nemt_token");
    const iatMs = token ? tokenIssuedAtMs(token) : null;
    if (iatMs && Date.now() - iatMs > REFRESH_STALE_MS && Date.now() - getLastActivity() <= idleLimit) {
      doRefresh();
    }
    const refreshTimer = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    return () => {
      WINDOW_ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener("visibilitychange", onActivity);
      clearInterval(idleTimer);
      clearInterval(refreshTimer);
    };
  }, [user, idleLogout]);

  const value = useMemo(
    () => ({ user, ready, login, verifyMfa, applySession, refreshUser, registerAgency, logout }),
    [user, ready, login, verifyMfa, applySession, refreshUser, registerAgency, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
